import Header from '../components/Header';
import Footer from '../components/Footer';
import { Target, Users, TrendingUp, Handshake, ShieldCheck, Zap } from 'lucide-react';

export default function AboutUs() {
  const missionItems = [
    { title: 'Inclusion', desc: 'Bringing world-class financial infrastructure to everyone, regardless of their credit history scale.', icon: Handshake, color: 'text-stripe-blue', bg: 'bg-stripe-bg' },
    { title: 'Speed', desc: 'Because opportunities don\'t wait, our technology is built to disburse capital in real-time.', icon: Zap, color: 'text-primary-500', bg: 'bg-primary-50' },
    { title: 'Accountability', desc: 'Total transparency in processing and pricing. What you see is what you pay.', icon: ShieldCheck, color: 'text-green-500', bg: 'bg-green-50' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-16 pb-32 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 text-center space-y-12">
           <div className="inline-flex items-center gap-3 px-4 py-2 bg-primary-50 rounded-full border border-primary-100">
              <span className="text-xs font-bold text-primary-600 uppercase tracking-widest leading-none">Our Story</span>
           </div>
           
           <h1 className="text-5xl md:text-7xl font-black text-stripe-dark leading-[1.05] tracking-tight max-w-4xl mx-auto italic">
             Financing built <br /> for <span className="text-primary-500">ambition</span>.
           </h1>
           
           <p className="text-xl text-stripe-slate leading-relaxed max-w-3xl mx-auto">
             Finova started with a simple belief: Traditional banking is too slow for the pace of modern business. We've redesigned the credit landscape from the ground up, combining high-fidelity technology with a human-centric mission.
           </p>
        </div>
        
        {/* Decorative background grid */}
        <div className="absolute inset-0 bg-stripe-bg -z-10 opacity-30 pointer-events-none" />
      </section>

      {/* Stats Section */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 -mt-32 relative z-20">
         <div className="bg-white p-12 lg:p-24 rounded-[48px] shadow-2xl border border-stripe-border grid md:grid-cols-3 gap-16 lg:gap-24">
            {[
              { label: 'Capital Disbursed', value: 'LKR 8.2B+', desc: 'Fueling local expansion since 2021.' },
              { label: 'Active Members', value: '12K+', desc: 'Growing with our protocol every day.' },
              { label: 'Success Rate', value: '99.5%', desc: 'Ensuring on-time repayment and growth.' }
            ].map((s, i) => (
              <div key={i} className="text-center md:text-left space-y-4">
                 <p className="text-xs font-black text-primary-500 uppercase tracking-widest">{s.label}</p>
                 <h4 className="text-4xl lg:text-5xl font-black text-stripe-dark tracking-tighter italic leading-none">{s.value}</h4>
                 <p className="text-sm text-stripe-slate leading-relaxed font-medium">{s.desc}</p>
              </div>
            ))}
         </div>
      </section>

      {/* Mission Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-24">
           <div className="grid lg:grid-cols-2 gap-24 items-center">
              <div className="space-y-10 group">
                 <h2 className="text-4xl lg:text-5xl font-black text-stripe-dark tracking-tighter leading-tight italic">
                   Architecting a more <span className="text-primary-500">efficient</span> capital market.
                 </h2>
                 <p className="text-lg text-stripe-slate leading-relaxed">
                   At Finova, we don't just provide loans; we provide the building blocks for individual and professional expansion. Our AI protocol identifies merit beyond traditional credit scores, rewarding discipline and focus.
                 </p>
                 <div className="space-y-4">
                   <div className="p-8 bg-stripe-bg/50 border border-stripe-border rounded-3xl hover:border-primary-500 transition-all cursor-default">
                      <p className="text-sm font-bold text-stripe-dark italic mb-2">Our Vision</p>
                      <p className="text-sm text-stripe-slate leading-relaxed">To become the default liquidity layer for Sri Lanka's growing digital economy, empowering the next million success stories.</p>
                   </div>
                 </div>
              </div>
              
              <div className="grid grid-cols-1 gap-8">
                 {missionItems.map((m, i) => (
                   <div key={i} className="s-card hover:bg-stripe-bg/20 transition-all duration-300">
                      <div className="flex items-start gap-8">
                         <div className={`w-14 h-14 ${m.bg} rounded-2xl shrink-0 flex items-center justify-center ${m.color} shadow-sm`}>
                            <m.icon size={28} />
                         </div>
                         <div className="space-y-3">
                            <h4 className="text-xl font-bold text-stripe-dark tracking-tight leading-none">{m.title}</h4>
                            <p className="text-sm text-stripe-slate leading-relaxed font-medium">{m.desc}</p>
                         </div>
                      </div>
                   </div>
                 ))}
              </div>
           </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
